import { Box, Center, Flex, Select, Text } from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { SortingRule } from "react-table";

import {
  IconButton,
  LoadingIndicator,
  SortableTable,
  SortableTableColumn,
} from "../../../../components";
import { generatePages } from "../../../../components/Table/Table";
import { PageOptions } from "../../../../types";
import { formatDateRange } from "../../../../utils/datetime";
import {
  AiInterviewCandidateFeedbackQuery,
  AiInterviewFeedbackSort,
  SortDirection,
  useAiInterviewCandidateFeedbackQuery,
} from "../../../graphql";

type FeedbackRow = NonNullable<
  AiInterviewCandidateFeedbackQuery["aiInterviewCandidateFeedback"]["results"]
>[number];

type AiInterviewerCandidateFeedbackTableProps = {
  aiInterviewerId: string;
  dateStart: Date;
  dateEnd: Date;
  pageSize: number;
  setPageSize: (size: number) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  sortColumn: AiInterviewFeedbackSort;
  setSortColumn: (col: AiInterviewFeedbackSort) => void;
  sortDirection: SortDirection;
  setSortDirection: (dir: SortDirection) => void;
  onEarliestDateLoaded?: (date: Date | null) => void;
};

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SORT_COLUMN_MAP: Record<string, AiInterviewFeedbackSort> = {
  createdAt: AiInterviewFeedbackSort.Date,
  candidateName: AiInterviewFeedbackSort.Candidate,
  candidateRating: AiInterviewFeedbackSort.Rating,
  duration: AiInterviewFeedbackSort.Duration,
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null) return "--";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const STAT_ROW_HEIGHT = "36px";

const ColumnHeader: React.FC<{
  label: string;
  stat?: string;
  showStatRow: boolean;
  textAlign?: "left" | "right";
  sortIndicator?: "asc" | "desc";
}> = ({ label, stat, showStatRow, textAlign = "left", sortIndicator }) => (
  <Box {...(textAlign === "right" ? { ml: "auto" } : {})}>
    <Text
      pb={showStatRow ? "8px" : "0"}
      lineHeight="16px"
      textAlign={textAlign}
      whiteSpace="nowrap"
    >
      {label}
      {sortIndicator && (
        <Text
          as="span"
          ml="8px"
          display="inline-block"
          transform={sortIndicator === "desc" ? "rotate(180deg)" : undefined}
        >
          ⌃
        </Text>
      )}
    </Text>
    {showStatRow && (
      <Box
        position="absolute"
        bottom={0}
        left={0}
        right={0}
        height={STAT_ROW_HEIGHT}
        bg="white"
        px={4}
        display="flex"
        alignItems="center"
        justifyContent={textAlign === "right" ? "flex-end" : "flex-start"}
        borderTop="1px solid"
        borderColor="gray.200"
        borderBottom="1px solid"
        borderBottomColor="#D3D8DE"
      >
        <Text
          fontSize="10px"
          lineHeight="12px"
          color="#808B97"
          fontWeight="semibold"
          letterSpacing="0.3px"
          textTransform="uppercase"
        >
          {stat ?? " "}
        </Text>
      </Box>
    )}
  </Box>
);

const PAGE_HOVER_CSS = {
  borderRadius: "full",
  background: "blue.50",
};

export const AiInterviewerCandidateFeedbackTable: React.FC<
  AiInterviewerCandidateFeedbackTableProps
> = ({
  aiInterviewerId,
  dateStart,
  dateEnd,
  pageSize,
  setPageSize,
  currentPage,
  setCurrentPage,
  sortColumn,
  setSortColumn,
  sortDirection,
  setSortDirection,
  onEarliestDateLoaded,
}) => {
  const { data, loading } = useAiInterviewCandidateFeedbackQuery({
    variables: {
      aiInterviewerId,
      dateStart: dateStart.toISOString(),
      dateEnd: dateEnd.toISOString(),
      sort: sortColumn,
      direction: sortDirection,
      pagination: {
        limit: pageSize,
        page: currentPage,
      },
    },
  });

  const feedbackResponse = data?.aiInterviewCandidateFeedback;
  const results = feedbackResponse?.results ?? [];
  const totalRows = feedbackResponse?.pageInfo?.totalRows ?? 0;
  const totalPages = feedbackResponse?.pageInfo?.totalPages ?? 1;

  useEffect(() => {
    if (!onEarliestDateLoaded) return;
    const raw = feedbackResponse?.earliestInterviewDate;
    onEarliestDateLoaded(raw ? new Date(raw) : null);
  }, [feedbackResponse?.earliestInterviewDate, onEarliestDateLoaded]);

  const handleSortChange = useCallback(
    (sortBy: SortingRule<Record<string, unknown>>[]) => {
      if (sortBy.length > 0) {
        const { id, desc: isDesc } = sortBy[0];
        const mapped = SORT_COLUMN_MAP[id];
        if (mapped) {
          setSortColumn(mapped);
          setSortDirection(isDesc ? SortDirection.Desc : SortDirection.Asc);
        }
      }
    },
    [setSortColumn, setSortDirection]
  );

  const sortOnlyPageOptions: PageOptions = useMemo(
    () => ({
      currentPage,
      totalPages: 1,
      handlePageNumberChange: setCurrentPage,
      onChangeSort: handleSortChange,
    }),
    [currentPage, setCurrentPage, handleSortChange]
  );

  const stats = useMemo(() => {
    const apiAvgRating = feedbackResponse?.avgRating;
    const apiAvgDuration = feedbackResponse?.avgDuration;
    return {
      avgRating: apiAvgRating != null ? apiAvgRating.toFixed(1) : null,
      avgDuration:
        apiAvgDuration != null
          ? formatDuration(Math.round(apiAvgDuration))
          : null,
      feedbackCount: feedbackResponse?.feedbackCount ?? 0,
      feedbackPct: feedbackResponse?.feedbackPct ?? 0,
    };
  }, [feedbackResponse]);

  const showStatRow = results.length > 0;

  const sortIndicatorFor = (
    col: AiInterviewFeedbackSort
  ): "asc" | "desc" | undefined =>
    sortColumn === col
      ? sortDirection === SortDirection.Asc
        ? "asc"
        : "desc"
      : undefined;

  const columns: SortableTableColumn<FeedbackRow>[] = useMemo(
    () => [
      {
        Header: (
          <ColumnHeader
            label="Interview date"
            showStatRow={showStatRow}
            sortIndicator={sortIndicatorFor(AiInterviewFeedbackSort.Date)}
          />
        ),
        accessor: "createdAt",
        id: "createdAt",
        Cell: ({ row: { original } }: { row: { original: FeedbackRow } }) => (
          <Text>
            {new Date(original.createdAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </Text>
        ),
      },
      {
        Header: (
          <ColumnHeader
            label="Candidate"
            stat={`${totalRows} candidates`}
            showStatRow={showStatRow}
            sortIndicator={sortIndicatorFor(AiInterviewFeedbackSort.Candidate)}
          />
        ),
        id: "candidateName",
        accessor: (row: FeedbackRow) => row.candidate?.fullName ?? "--",
      },
      {
        Header: (
          <ColumnHeader
            label="Rating"
            stat={stats.avgRating ? `avg ${stats.avgRating}★` : undefined}
            showStatRow={showStatRow}
            textAlign="right"
            sortIndicator={sortIndicatorFor(AiInterviewFeedbackSort.Rating)}
          />
        ),
        accessor: "candidateRating",
        id: "candidateRating",
        Cell: ({ row: { original } }: { row: { original: FeedbackRow } }) => {
          if (original.candidateRating == null) return <Text>--</Text>;
          return (
            <Text textAlign="right">
              <Text as="span">{original.candidateRating}&nbsp;</Text>
              <Text as="span" color="#FFA300">
                ★
              </Text>
            </Text>
          );
        },
      },
      {
        Header: (
          <ColumnHeader
            label="Duration"
            stat={stats.avgDuration ? `avg ${stats.avgDuration}` : undefined}
            showStatRow={showStatRow}
            sortIndicator={sortIndicatorFor(AiInterviewFeedbackSort.Duration)}
          />
        ),
        accessor: "duration",
        id: "duration",
        Cell: ({ row: { original } }: { row: { original: FeedbackRow } }) => (
          <Text>{formatDuration(original.duration)}</Text>
        ),
      },
      {
        Header: (
          <ColumnHeader
            label="Feedback"
            stat={`${stats.feedbackCount} comments (${stats.feedbackPct}%)`}
            showStatRow={showStatRow}
          />
        ),
        accessor: "candidateFeedback",
        id: "candidateFeedback",
        disableSortBy: true,
        Cell: ({ row: { original } }: { row: { original: FeedbackRow } }) => (
          <Text>{original.candidateFeedback || ""}</Text>
        ),
      },
    ],
    [stats, showStatRow, sortColumn, sortDirection, totalRows]
  );

  const dateRangeDisplay = formatDateRange(dateStart, dateEnd);

  const tableWrapperSx = {
    "& > div": {
      boxShadow: "none !important",
      borderRadius: "12px",
    },
    "& tbody tr:last-child": {
      borderBottom: "none",
    },
    "& th:not(:last-child), & td:not(:last-child)": {
      width: "1%",
      whiteSpace: "nowrap",
    },
    "& th": {
      fontSize: "12px !important",
      lineHeight: "16px",
      color: "#67717E !important",
      "& .chakra-icon": {
        display: "none !important",
      },
      ...(showStatRow
        ? {
            position: "relative",
            pb: STAT_ROW_HEIGHT,
            verticalAlign: "top",
          }
        : {}),
    },
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.300"
      borderRadius="12px"
      p={6}
      bg="white"
    >
      <Flex direction="column" gap={4}>
        <Flex justifyContent="space-between" alignItems="center">
          <Text>
            <Text as="span" fontWeight="semibold">
              All candidate feedback
            </Text>{" "}
            <Text as="span" fontSize="sm" color="gray.500">
              (
              <Text as="span" fontStyle="italic">
                n
              </Text>
              ={totalRows})
            </Text>
          </Text>
          <Text fontSize="sm" color="gray.600">
            {dateRangeDisplay}
          </Text>
        </Flex>
        <Flex justifyContent="space-between" alignItems="center">
          <Box />
          <Flex alignItems="center" gap={2}>
            <Text fontSize="sm" color="gray.600">
              Showing
            </Text>
            <Select
              size="sm"
              w="80px"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </Select>
          </Flex>
        </Flex>
        <Box sx={tableWrapperSx}>
          <SortableTable<FeedbackRow>
            columns={columns}
            data={results}
            initialSort={{ id: "createdAt", desc: true }}
            manualPagination
            pageOptions={sortOnlyPageOptions}
            autoResetSortBy={false}
            width="100%"
          />
        </Box>
      </Flex>
      {loading && (
        <Center py="80px">
          <LoadingIndicator delay={0} />
        </Center>
      )}
      {totalPages > 1 && (
        <Center mt={4}>
          <Flex alignItems="center" gap={1}>
            <IconButton
              aria-label="Previous Page"
              icon={<MdChevronLeft />}
              size="sm"
              variant="ghost"
              color="gray.500"
              fontSize="20px"
              isDisabled={currentPage === 1}
              _hover={PAGE_HOVER_CSS}
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            />
            {generatePages(totalPages, currentPage).map((page, index) => {
              if (page === 0) {
                return (
                  <Flex
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    w="32px"
                    h="32px"
                    alignItems="center"
                    justifyContent="center"
                    color="gray.500"
                  >
                    ...
                  </Flex>
                );
              }
              const isActive = page === currentPage;
              return (
                <Flex
                  as="button"
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  w="32px"
                  h="32px"
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight={isActive ? "semibold" : "normal"}
                  color={isActive ? "blue.600" : "gray.600"}
                  bg={isActive ? "blue.50" : "transparent"}
                  _hover={isActive ? {} : PAGE_HOVER_CSS}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </Flex>
              );
            })}
            <IconButton
              aria-label="Next Page"
              icon={<MdChevronRight />}
              size="sm"
              variant="ghost"
              color="gray.500"
              fontSize="20px"
              isDisabled={currentPage === totalPages}
              _hover={PAGE_HOVER_CSS}
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
            />
          </Flex>
        </Center>
      )}
    </Box>
  );
};
